import { useEffect, useMemo, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getAuthToken, getPortalType } from '@/utils/auth';

export default function PDFViewerModal({ isOpen, onClose, pdfUrl, title, initialPage = 1 }) {
    const [loading, setLoading] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    const cacheUrl = useMemo(() => {
        if (!pdfUrl) return null;
        // Bypassing backend cache proxy to resolve 504 Gateway Timeout issues.
        // The original pdfUrl is used directly.
        return `${pdfUrl}${pdfUrl.includes('?') ? '&' : '?'}_=${reloadKey}`;
    }, [pdfUrl, reloadKey]);

    const iframeSrc = useMemo(() => {
        if (!cacheUrl) return null;
        return `${cacheUrl}#page=${initialPage}&view=FitH&navpanes=0&pagemode=none`;
    }, [cacheUrl, initialPage]);

    useEffect(() => {
        if (isOpen && iframeSrc) {
            setLoading(true);
        }
    }, [isOpen, iframeSrc]);

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 gap-0 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] flex flex-col">
                <DialogHeader className="p-4 border-b border-[hsl(var(--card-border))] flex flex-row items-center justify-between space-y-0 shrink-0">
                    <DialogTitle className="text-foreground">{title || 'Textbook Viewer'}</DialogTitle>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={onClose} className="text-muted-foreground hover:text-foreground">
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                </DialogHeader>

                <div className="flex-1 w-full h-full bg-gray-900 relative overflow-hidden">
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-[hsl(var(--card-bg))]">
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
                                <p className="text-muted-foreground">Loading PDF...</p>
                            </div>
                        </div>
                    )}

                    {iframeSrc ? (
                        <iframe
                            src={iframeSrc}
                            className="w-full h-full border-0"
                            title="PDF Viewer"
                            onLoad={() => setLoading(false)}
                            onError={() => setLoading(false)}
                        />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-[hsl(var(--card-bg))]">
                            <div className="text-center">
                                <p className="text-muted-foreground mb-4">No PDF selected.</p>
                            </div>
                        </div>
                    )}

                    {iframeSrc && !loading && (
                        <div className="absolute bottom-3 right-3 z-10">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setLoading(true);
                                    setReloadKey((k) => k + 1);
                                }}
                            >
                                Reload
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
